import React, { useEffect, useState } from 'react';
import '../styles/Dice3D.css';

interface Dice3DProps {
  value: number; // 1-6, the final dice value from backend
  onComplete?: () => void; // Optional - only last dice needs this
  duration: number; // total animation duration in ms
  diceIndex?: number; // Index for staggered animation (0, 1, 2...)
}

// Rotation vectors for each dice face (1-6)
const FACE_ROTATIONS: Record<number, [number, number, number]> = {
  1: [-0.1, 0.3, -1],
  2: [-0.1, 0.6, -0.4],
  3: [-0.85, -0.42, 0.73],
  4: [-0.8, 0.3, -0.75],
  5: [0.3, 0.45, 0.9],
  6: [-0.16, 0.6, 0.18],
};

const Dice3D: React.FC<Dice3DProps> = ({ value, onComplete, duration, diceIndex = 0 }) => {
  const [phase, setPhase] = useState<'waiting' | 'rolling' | 'throwing' | 'complete'>('waiting');

  // Stagger animation start by 150ms per dice
  const staggerDelay = diceIndex * 150;

  // Calculate timing: roll for most of the duration, then throw to land, then pause
  const rollDuration = Math.max(duration - 1800, 1000); // Reserve time for throw + pause
  const throwDuration = 1000;
  const pauseDuration = 800; // Pause after dice lands before transitioning

  useEffect(() => {
    // Start with waiting state for stagger
    setPhase('waiting');

    // Start rolling after stagger delay
    const startTimer = setTimeout(() => {
      setPhase('rolling');
    }, staggerDelay);

    // After roll duration, switch to throwing (which lands on the value)
    const throwTimer = setTimeout(() => {
      setPhase('throwing');
    }, staggerDelay + rollDuration);

    // Mark as complete after throw finishes
    const completeTimer = setTimeout(() => {
      setPhase('complete');
    }, staggerDelay + rollDuration + throwDuration);

    // Call onComplete after pause so user can see the result
    const callbackTimer = setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, staggerDelay + rollDuration + throwDuration + pauseDuration);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(throwTimer);
      clearTimeout(completeTimer);
      clearTimeout(callbackTimer);
    };
  }, [value, staggerDelay, rollDuration, throwDuration, pauseDuration, onComplete]);

  // Get the rotation for the target value
  const rotation = FACE_ROTATIONS[value] || FACE_ROTATIONS[1];
  const transformStyle =
    phase === 'throwing' || phase === 'complete'
      ? { transform: `rotate3d(${rotation.join(', ')}, 180deg)` }
      : undefined;

  const wrapperClass = phase === 'rolling' ? 'rolling' : phase === 'waiting' ? 'waiting' : '';

  return (
    <div className={`dice-3d-wrapper ${wrapperClass}`}>
      <div
        className={`dice-3d ${phase}`}
        style={transformStyle}
      >
        <div className="dice-face front" />
        <div className="dice-face up" />
        <div className="dice-face left" />
        <div className="dice-face right" />
        <div className="dice-face bottom" />
        <div className="dice-face back" />
      </div>
    </div>
  );
};

export default Dice3D;
